const HASH_PREFIX = "pbkdf2-sha256-v1";
const HASH_ITERATIONS = 100_000;
const MIN_HASH_ITERATIONS = 10_000;
const HASH_SIZE_BITS = 256;
const SALT_SIZE_BYTES = 16;

const getCrypto = (): Crypto => {
  const crypto = globalThis.crypto;

  if (!crypto?.subtle) {
    throw new Error("Web Crypto is not available");
  }

  return crypto;
};

const toHex = (value: Uint8Array): string => {
  return Array.from(value, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const fromHex = (value: string): Uint8Array<ArrayBuffer> => {
  const bytes = new Uint8Array(value.length / 2);

  for (let index = 0; index < bytes.length; index++) {
    bytes[index] = Number.parseInt(value.slice(index * 2, index * 2 + 2), 16);
  }

  return bytes;
};

const deriveHash = async (
  data: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number
): Promise<string> => {
  const crypto = getCrypto();
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(data), "PBKDF2", false, [
    "deriveBits",
  ]);
  const hash = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations,
    },
    key,
    HASH_SIZE_BITS
  );

  return toHex(new Uint8Array(hash));
};

export const hashString = async (data: string): Promise<string> => {
  const salt = getCrypto().getRandomValues(new Uint8Array(SALT_SIZE_BYTES));
  const hash = await deriveHash(data, salt, HASH_ITERATIONS);

  return `${HASH_PREFIX}$${HASH_ITERATIONS}$${toHex(salt)}$${hash}`;
};

export const compareString = async (data: string, hash: string): Promise<boolean> => {
  const parts = hash.split("$");
  const [prefix, iterationsValue, salt, expectedHash] = parts;
  const iterations = Number(iterationsValue);

  if (parts.length !== 4 || prefix !== HASH_PREFIX) {
    return false;
  }

  if (!Number.isInteger(iterations) || String(iterations) !== iterationsValue) {
    return false;
  }

  if (iterations < MIN_HASH_ITERATIONS || iterations > HASH_ITERATIONS) {
    return false;
  }

  if (salt.length !== 32 || /[^a-f0-9]/.test(salt)) {
    return false;
  }

  if (expectedHash.length !== 64 || /[^a-f0-9]/.test(expectedHash)) {
    return false;
  }

  const actualHash = await deriveHash(data, fromHex(salt), iterations);
  let difference = 0;

  for (let index = 0; index < expectedHash.length; index++) {
    difference |= actualHash.charCodeAt(index) ^ expectedHash.charCodeAt(index);
  }

  return difference === 0;
};
